import React from 'react';
import { motion } from 'framer-motion';
import {
  FaMoneyBillWave, FaCarSide, FaPercent, FaCalendarAlt,
  FaTimes, FaHeadset, FaAward, FaCarAlt
} from 'react-icons/fa';
import bgImage from '../../assets/images/What-to-do-in-Athens-Plaka.jpg';

const iconColors = [
  "text-green-300 bg-green-900/40",
  "text-indigo-300 bg-indigo-900/40",
  "text-pink-300 bg-pink-900/40",
  "text-yellow-300 bg-yellow-900/40",
  "text-red-300 bg-red-900/40",
  "text-blue-300 bg-blue-900/40",
  "text-purple-300 bg-purple-900/40",
  "text-teal-300 bg-teal-900/40",
];

const FeatureCard = ({ icon: Icon, title, description, index }) => (
  <motion.div
    className="flex flex-col items-center text-center p-7 bg-white/20 backdrop-blur-lg rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-white/30 relative group overflow-hidden"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
  >
    <motion.div
      className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 text-4xl shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6 ${iconColors[index % iconColors.length]}`}
      whileHover={{ scale: 1.15, rotate: 8 }}
    >
      <Icon />
    </motion.div>
    <h3 className="text-xl font-extrabold mb-2 text-white drop-shadow-lg tracking-wide">
      {title}
    </h3>
    <p className="text-white/90 text-base font-medium drop-shadow-md">
      {description}
    </p>
    {/* Decorative Glow */}
    <span className="absolute -bottom-8 -right-8 w-20 h-20 bg-gradient-to-br from-white/10 to-indigo-400/20 rounded-full blur-2xl opacity-60 group-hover:opacity-80 transition-all duration-300" />
  </motion.div>
);

const Features = () => {
  const features = [
    {
      icon: FaMoneyBillWave,
      title: "Fixed Rates",
      description: "Enjoy transparent, upfront pricing for all Athens transfers-no hidden fees, ever.",
    },
    {
      icon: FaCarSide,
      title: "Reliable Transfers",
      description: "Travel with confidence! Our professional drivers and modern vehicles ensure safety and punctuality.",
    },
    {
      icon: FaPercent,
      title: "No Booking Fees",
      description: "Book direct and save-no extra charges, just exceptional value for your Athens journey.",
    },
    {
      icon: FaCalendarAlt,
      title: "Booking Flexibility",
      description: "Plans changed? No problem. Modify your Athens transfer booking anytime, hassle-free.",
    },
    {
      icon: FaTimes,
      title: "Free Cancellation",
      description: "Cancel up to THREE hours before your scheduled Athens transfer-no penalties, no worries.",
    },
    {
      icon: FaHeadset,
      title: "24/7 Customer Service",
      description: "Our Athens-based support team is always available to assist you, day or night.",
    },
    {
      icon: FaAward,
      title: "Award-Winning Service",
      description: "Voted Athens' most reliable transfer provider-trusted by thousands of happy travelers.",
    },
    {
      icon: FaCarAlt,
      title: "Luxury Vehicles",
      description: "Relax in style with our fleet of pristine, comfortable vehicles for every Athens transfer.",
    },
  ];

  return (
    <section className="relative overflow-hidden">
      {/* Background Image with Gradient Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(30,41,59,0.9) 10%, rgba(0,0,0,0.95) 100%), url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        aria-hidden="true"
      />
      {/* Decorative Gradient Blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-400/30 rounded-full blur-3xl z-0 animate-pulse-slow" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl z-0 animate-pulse-slow" />

      <div className="py-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-4xl md:text-5xl font-extrabold text-center mb-8 text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 via-white to-blue-200 drop-shadow-2xl"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Why Choose Our Athens Transfer Service?
          </motion.h2>
          <motion.p
            className="max-w-2xl mx-auto text-lg text-center text-white/80 mb-14 font-medium"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Experience the difference with Athens’ leading transfer company. From airport pickups to city tours, we deliver exceptional comfort, reliability, and value-every time you travel.
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
